import { type FormEvent, useState } from 'react';
import { analytics } from '../utils/analytics';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const FORM_NAME = 'feedback';

export function NetlifyFeedbackForm() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (status === 'submitting') {
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.set('page_url', window.location.href);

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        body.append(key, value);
      }
    });

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) {
        throw new Error(`Feedback request failed with ${response.status}`);
      }

      analytics.track('feedback_submitted', { location: 'footer' });
      form.reset();
      setStatus('success');
    } catch (error) {
      console.error('[Feedback] Failed to submit feedback:', error);
      analytics.track('feedback_submit_failed', { location: 'footer' });
      setErrorMessage('Feedback could not be sent. Please try again.');
      setStatus('error');
    }
  };

  return (
    <section id="feedback-form" className="border-t border-gray-200 bg-white">
      <div className="mx-auto grid max-w-7xl gap-5 px-4 py-6 sm:px-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-start lg:px-8">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Feedback</h2>
          <p className="mt-1 max-w-md text-sm text-gray-600">
            Share bugs, confusing moments, or forecasting workflows you want supported.
          </p>
        </div>

        <form
          name={FORM_NAME}
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
          className="grid gap-3"
        >
          <input type="hidden" name="form-name" value={FORM_NAME} />
          <input type="hidden" name="page_url" defaultValue="" />
          <p className="absolute m-[-1px] h-px w-px overflow-hidden border-0 p-0 [clip:rect(0_0_0_0)]">
            <label>
              Do not fill this out: <input name="bot-field" tabIndex={-1} />
            </label>
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1 text-sm font-medium text-gray-700">
              Name
              <input
                name="name"
                type="text"
                autoComplete="name"
                className="h-10 rounded-md border border-gray-300 px-3 text-sm font-normal text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>
            <label className="grid gap-1 text-sm font-medium text-gray-700">
              Email
              <input
                name="email"
                type="email"
                autoComplete="email"
                className="h-10 rounded-md border border-gray-300 px-3 text-sm font-normal text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>
          </div>

          <label className="grid gap-1 text-sm font-medium text-gray-700">
            Message
            <textarea
              name="message"
              required
              rows={3}
              maxLength={2000}
              placeholder="What should be improved?"
              className="resize-y rounded-md border border-gray-300 px-3 py-2 text-sm font-normal text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div aria-live="polite" className="min-h-5 text-sm">
              {status === 'success' && (
                <span className="font-medium text-green-700">Thanks. Feedback sent.</span>
              )}
              {status === 'error' && (
                <span className="font-medium text-red-700">{errorMessage}</span>
              )}
            </div>
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="inline-flex h-10 items-center justify-center rounded-md bg-gray-900 px-4 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {status === 'submitting' ? 'Sending...' : 'Send feedback'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
